import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { LoggingService } from '@services/logging.service';
import { StudyService } from '@services/study.service';
import { SearchStrategyService } from '@services/search-strategy.service';
import { StudySearchStrategy } from '@models/searchStrategy.model';
import { ResultMessage } from '@shared/_constants';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { DialogService } from '@services/dialog.service';
import { AcknowledgeMessage } from '@shared/_constants';
import { TreeNode } from 'primeng/api';
import { ReportsService } from '@services/reports.service';
import { MasterMatrixService } from '../../../services/mastermatrix.service';
import { FinancialAdjustmentFilter } from '../../../models/financialAdjustmentfilter.model';
import { ReportTemplates } from '../../_study_constants';
import { TestedParty } from '../../analysis/adjustments/tested-party/tested-party.model';
import { TestedPartyService } from '../../analysis/adjustments/tested-party/tested-party.service';
import { ContemporaryReportType } from './contemporary-report-type.enum';

@Component({
  selector: 'app-study-reports-modal',
  templateUrl: './study-reports-modal.component.html',
  styleUrls: ['./study-reports-modal.component.css'],
  providers: [SearchStrategyService],
})
export class StudyReportsModalComponent implements OnInit, OnDestroy {
  searchStrategy: StudySearchStrategy;
  financialAdjustmentFilter: FinancialAdjustmentFilter = new FinancialAdjustmentFilter();
  private _success = new Subject<string>();
  message = '';
  type = 'success';
  blockThisUI = false;
  testedParties: TestedParty[];
  selectedTestedParty: TestedParty;
  showGeneralInfo = true;
  showTestedPartiesSelection = false;
  showContemporaryOptions = false;

  public reportTemplate: ReportTemplates; // coordinate variable with dialog.service.ts
  public reportOptions: any; // coordinate variable with dialog.service.ts
  public reportType: any = ReportTemplates;
  public defaultColumns: any[];
  public optionalColumns: TreeNode[];
  public selections: any[] = [];
  contemporaryReportTypeSelection: ContemporaryReportType = ContemporaryReportType.Ytd;

  private destroy$ = new Subject();

  constructor(
    private activeModal: NgbActiveModal,
    private logger: LoggingService,
    private searchStrategyService: SearchStrategyService,
    private studyService: StudyService,
    private dialogService: DialogService,
    private reportsService: ReportsService,
    private masterMatrixService: MasterMatrixService,
    private testedPartyService: TestedPartyService,
  ) {}

  ngOnInit() {
    this.searchStrategy = this.searchStrategyService.studySearchStrategy;

    if (this.loadTestedPartiesData) {
      this.getTestedParties();
    }
    if (this.isDetailedPLISummaryReport || this.isEngagementCodeReport || this.isSearchWriteUpReport || this.isContemporaryFinancialSummaryReport) {
      this.showGeneralInfo = false;
    }

    if (this.isContemporaryFinancialSummaryReport) {
      this.showContemporaryOptions = true;
    }

    this._success.subscribe((message) => (this.message = message));
    this._success.pipe(debounceTime(5000)).subscribe(() => (this.message = ''));

    const reportSettings = this.reportsService.getReportSettings(this.reportTemplate);
    this.defaultColumns = reportSettings.defaultColumns;
    this.optionalColumns = reportSettings.optionalColumns;
  }

  getTestedParties() {
    this.blockThisUI = true;

    this.testedPartyService
      .getCompletedTestedParties(this.searchStrategy.studySearchStrategyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((_testedParties) => {
        if (_testedParties && _testedParties.length > 0) {
          this.testedParties = _testedParties;
          this.selectedTestedParty = this.testedParties[0];
        }
        this.showTestedPartiesSelection = true;
        this.blockThisUI = false;
      });
  }

  public generatePreliminaryReport() {
    this.generateReport(false);
  }

  public generateFinalReport() {
    this.generateReport(true);
  }

  public close() {
    this.activeModal.close();
  }

  public async generateReport(isFinal: boolean) {
    this.blockThisUI = true;
    try {
      // run reports asynchronously, no "await"
      if (this.reportTemplate === ReportTemplates.SearchStrategy) {
        this.studyService.ReportSearchStrategy(this.searchStrategy, isFinal, this.selections);
      } else if (this.reportTemplate === ReportTemplates.KeywordScreening) {
        this.studyService.ReportKeywordScreening(this.searchStrategy, isFinal, this.selections);
      } else if (this.reportTemplate === ReportTemplates.InternetReview) {
        this.studyService.ReportIRMatrix(this.searchStrategy, isFinal, this.selections);
      } else if (this.reportTemplate === ReportTemplates.AcceptedCompanies) {
        this.studyService.ReportAcceptedCompanies(this.searchStrategy, isFinal, this.selections);
      } else if (this.reportTemplate === ReportTemplates.AcceptedRejectedCompanies) {
        this.studyService.ReportAcceptedRejectedCompanies(this.searchStrategy, isFinal, this.selections);
      } else if (this.reportTemplate === ReportTemplates.EliminationChart) {
        this.studyService.ReportEliminationChart(this.searchStrategy, isFinal, this.selections);
      } else if (this.reportTemplate === ReportTemplates.FinancialAdjustments) {
        this.financialAdjustmentFilter.studyId = this.searchStrategy.studyId;
        this.financialAdjustmentFilter.studySearchStrategyId = this.searchStrategy.studySearchStrategyId;
        this.masterMatrixService.ReportPLISummary(this.financialAdjustmentFilter, isFinal, this.selections);
      } else if (this.isEngagementCodeReport) {
        this.studyService.ReportEngagementCode(this.searchStrategy.studyId);
      } else if (this.isContemporaryFinancialSummaryReport) {
        this.studyService.ContemporaryFinancialSummaryReport(
          this.searchStrategy,
          this.getSelectedTestedPartyId,
          this.contemporaryReportTypeSelection,
        );
      } else if (this.reportTemplate === ReportTemplates.FinancialAutoAdjustments) {
        this.financialAdjustmentFilter.studyId = this.searchStrategy.studyId;
        this.financialAdjustmentFilter.studySearchStrategyId = this.searchStrategy.studySearchStrategyId;
        this.masterMatrixService.ReportPLISummaryAutoAdjustments(
          this.financialAdjustmentFilter,
          isFinal,
          this.selections,
          this.getSelectedTestedPartyId,
        );
      } else if (this.isDetailedPLISummaryReport) {
        this.masterMatrixService.reportDetailedPLISummary(
          this.searchStrategy.studyId,
          this.searchStrategy.studySearchStrategyId,
          this.getSelectedTestedPartyId,
        );
      } else if (this.isSearchWriteUpReport) {
        this.studyService.ReportSearchWriteUp(this.searchStrategy);
      } else {
        const msg = `Unknown Report:[${this.reportTemplate ? `${this.reportOptions.name}` : 'null'}] Report Type:[${this.reportTemplate}].`;
        this.logger.warn(this.constructor.name, 'generateReport', msg);
        this._success.next(msg);
        this.type = 'danger';
      }
      await this.dialogService.acknowledge('Processing Report ...', AcknowledgeMessage.ReportGenerationStarted, 'OK');
      this.blockThisUI = false;
      this.close();
      return true;
    } catch (e) {
      this.logger.error(this.constructor.name, 'generateReport', e);
      this._success.next(ResultMessage.UnexpectedError);
      this.type = 'danger';
      this.blockThisUI = false;
      return true;
    }
  }

  isSelectionAvailable() {
    return (
      this.reportTemplate !== this.reportType.AcceptedCompanies &&
      this.reportTemplate !== this.reportType.AcceptedRejectedCompanies &&
      !this.isEngagementCodeReport &&
      !this.isDetailedPLISummaryReport &&
      !this.isSearchWriteUpReport &&
      !this.isContemporaryFinancialSummaryReport
    );
  }

  isSingleReport() {
    return this.isEngagementCodeReport || this.isSearchWriteUpReport || this.isContemporaryFinancialSummaryReport;
  }

  generateGeneralReport() {
    this.generateReport(false);
  }

  get getSelectedTestedPartyId(): number {
    return this.selectedTestedParty ? this.selectedTestedParty.adjustmentsTestedPartyId : 0;
  }

  get loadTestedPartiesData(): boolean {
    return (
      this.reportTemplate === this.reportType.FinancialAutoAdjustments || this.isDetailedPLISummaryReport || this.isContemporaryFinancialSummaryReport
    );
  }

  get hasTestedParties(): boolean {
    return !!this.testedParties && this.testedParties.length > 0;
  }

  get isDetailedPLISummaryReport(): boolean {
    return this.reportTemplate === this.reportType.DetailedPLISummary;
  }

  get isEngagementCodeReport(): boolean {
    return this.reportTemplate === this.reportType.EngagementCodeReport;
  }

  get isSearchWriteUpReport(): boolean {
    return this.reportTemplate === this.reportType.SearchWriteUp;
  }

  get isContemporaryFinancialSummaryReport(): boolean {
    return this.reportTemplate === this.reportType.ContemporaryFinancialSummaryReport;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }
}
